/**
 * Ficha de um ponto de qualidade do ar: sensor de baixo custo (de qualquer
 * rede) ou estação do MonitorAr.
 *
 * Como nas outras fichas, o valor nunca aparece sozinho: vem com a faixa, o
 * horário da medição (Brasília e UTC) e, no sensor, o que o número é e de
 * quem é a escala — cada rede aparece como ela mesma publica, na unidade e
 * nas faixas dela (`faixas.ts`).
 */
import { ExternalLink } from 'lucide-react';
import { useMemo } from 'react';
import { formatLonLat } from '../lib/format';
import { formatDateBrt, formatDateTimeBoth, formatTimeBrt, HOUR_MS, MINUTE_MS } from '../lib/time';
import { shownTimeOf, useTimeStore } from '../state/time';
import { linkDoSensor, MONITORAR_URL, NOME_REDE, PURPLEAIR_ATRIBUICAO, PURPLEAIR_URL, rotuloRef } from './api';
import {
  COR_SEM_DADO,
  ESCALAS,
  OMS_PM25_24H,
  corDaFaixa,
  faixaDaEstacao,
  faixaNaEscala,
  mediaComCobertura,
  nomeDaFaixa,
  pmTexto,
  referenciasDaEscala,
  referenciasUgm3,
  valorNaEscala,
  type EscalaId,
} from './faixas';
import { Grafico, type Linha } from './Grafico';
import type { PontoFeature } from './layer';
import { Simbolo } from './Simbolo';
import { useArStore } from './store';
import {
  espalharHoras,
  estenderLeituras,
  indiceQueContem,
  janela24h,
  parado,
  TOLERANCIA_ESTACAO_MS,
  TOLERANCIA_SENSOR_MS,
  ultimoAte,
} from './tempo';
import { FL_AB, escalaDoSensor, textoBruto } from './sensor';
import type { Estacao, IqarHora, Sensor } from './types';
import { ProximosDiasAqui } from '../previsao/ProximosDiasAqui';

const REF_IQAR = referenciasDaEscala('iqar');

/**
 * O valor publicado como a rede o mostra: "12,6 µg/m³"; na PurpleAir, o AQI
 * ("AQI 52"), com a concentração de onde ele sai à parte.
 */
function numeroNaEscala(esc: EscalaId, ugm3: number): string {
  if (esc === 'purpleair') return `AQI ${valorNaEscala(esc, ugm3) ?? '—'}`;
  return `${pmTexto(ugm3, ESCALAS[esc].casas)} µg/m³`;
}

/** Cores das linhas dos poluentes, distintas das faixas. */
const COR_POLUENTE = ['#7fe3ff', '#ff9fd0', '#b3f07a', '#c9a7ff', '#ffd9a0', '#9fb4ff'];

function Linha_(props: { rotulo: string; children: React.ReactNode; nota?: React.ReactNode; titulo?: string | null }) {
  const { rotulo, children, nota } = props;
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="shrink-0 text-[11px] text-ink-400" title={props.titulo ?? undefined}>
        {rotulo}
      </span>
      <span className="text-right text-xs text-ink-100">
        {children}
        {nota && <span className="block text-[10px] text-ink-400">{nota}</span>}
      </span>
    </div>
  );
}

function Fonte({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 text-rio-300 hover:underline">
      {children}
      <ExternalLink size={10} />
    </a>
  );
}

function useInstante(): number | null {
  return useTimeStore((s) => shownTimeOf(s));
}

// ---------------------------------------------------------------------------
// Sensor

function FichaSensor({ inicial }: { inicial: Sensor }) {
  // A ficha lê o sensor da store, e não do clique: se os dados se renovam com
  // a ficha aberta, a série nova tem de vir com o `t0` novo.
  const s = useArStore((st) => st.pontos.sensores.find((x) => x.id === inicial.id)) ?? inicial;
  const t0 = useArStore((st) => st.pontos.t0);
  const passoMin = useArStore((st) => st.pontos.passoMin);
  const municipios = useArStore((st) => st.pontos.municipios);
  const municipiosH0 = useArStore((st) => st.pontos.municipiosH0);
  const instante = useInstante();
  const passoMs = passoMin * MINUTE_MS;
  const n = s.serie.length;
  const tolMs = s.tolMs ?? TOLERANCIA_SENSOR_MS;
  const horaria = tolMs > TOLERANCIA_SENSOR_MS;

  const noQuadro = ultimoAte(s.serie, t0, passoMs, instante, tolMs);
  // Sem leitura perto do quadro, a ficha mostra a última até ele, com o horário — nunca a do futuro.
  const exibida =
    noQuadro ??
    ultimoAte(s.serie, t0, passoMs, instante ?? Date.now(), Infinity) ??
    (s.ultima.pm !== null && (instante === null || s.ultima.t <= instante) ? { valor: s.ultima.pm, t: s.ultima.t, i: -1 } : null);
  const esc = escalaDoSensor(s.fonte);
  const faixa = faixaNaEscala(esc, exibida?.valor);
  const cor = faixa ? corDaFaixa(faixa) : COR_SEM_DADO;
  const estaParado = parado(s.ultima.t);

  // A média horária por município é da rede do Acre: só serve a sensores do Acre.
  const horariaMun = s.municipio && s.uf === 'AC' ? municipios.get(s.municipio) : undefined;
  const serieMun = useMemo(
    () => (horariaMun ? espalharHoras(horariaMun, municipiosH0, t0, passoMs, n) : null),
    [horariaMun, municipiosH0, t0, passoMs, n],
  );
  const iAtual = indiceQueContem(n, t0, passoMs, instante);
  const iMun = horariaMun ? indiceQueContem(horariaMun.length, municipiosH0, HOUR_MS, instante) : -1;

  // Média de 24 h: a do sensor, com cobertura; senão a do município (média horária da rede).
  const media24 = useMemo(() => {
    const fim = iAtual >= 0 ? iAtual : n - 1;
    const doSensor = mediaComCobertura(janela24h(horaria ? estenderLeituras(s.serie, passoMs, tolMs) : s.serie, fim, passoMs));
    if (doSensor !== null) return { valor: doSensor, de: 'sensor' as const };
    if (horariaMun) {
      const fimH = iMun >= 0 ? iMun : horariaMun.length - 1;
      const doMun = mediaComCobertura(janela24h(horariaMun, fimH, HOUR_MS));
      if (doMun !== null) return { valor: doMun, de: 'municipio' as const };
    }
    return null;
  }, [s.serie, iAtual, n, passoMs, horariaMun, iMun, horaria, tolMs]);

  const serieDesenho = useMemo(() => (horaria ? estenderLeituras(s.serie, passoMs, tolMs) : s.serie), [s.serie, horaria, passoMs, tolMs]);
  const linhas = useMemo<Linha[]>(() => {
    const l: Linha[] = [{ valores: serieDesenho, cor: faixa ? corDaFaixa(faixa) : '#e6edf7' }];
    if (serieMun) l.push({ valores: serieMun, cor: 'rgba(200,210,225,0.75)', tracejado: true });
    return l;
  }, [serieDesenho, serieMun, faixa]);
  const instantes = useMemo(() => Array.from({ length: n }, (_, i) => t0 + i * passoMs), [n, t0, passoMs]);
  const temSerie = s.serie.some((v) => v !== null) || (serieMun?.some((v) => v !== null) ?? false);

  const lugar = [s.municipio, s.uf].filter(Boolean).join(' – ');
  const titulo = s.fonte === 'ufac' && s.municipio ? `${s.municipio}${s.cod ? ` · ${s.cod}` : ''}` : (s.nome ?? lugar) || `Sensor ${s.ref}`;
  const subtitulo = s.fonte === 'ufac' ? s.nome : lugar || null;
  const bruto = textoBruto(s.bruto);
  const referencias = useMemo(() => referenciasUgm3(esc), [esc]);

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5 text-xs text-ink-400">
        <Simbolo forma="hexagono" cor={faixa ? cor : null} tamanho={12} />
        Qualidade do ar · sensor de baixo custo
      </div>

      <div className="leading-tight font-semibold text-white">
        {titulo}
        {subtitulo && <div className="text-[11px] font-normal text-ink-400">{subtitulo}</div>}
        <div className="text-[11px] font-normal text-ink-300">
          {NOME_REDE[s.fonte]}
          {s.dono && (
            <>
              {' · '}
              responsável: {s.dono}
            </>
          )}
        </div>
      </div>

      {exibida ? (
        <div>
          <div className="flex items-baseline gap-2 pt-0.5">
            <span className="text-xl font-semibold tabular-nums" style={{ color: cor }}>
              {numeroNaEscala(esc, exibida.valor)}
            </span>
            {faixa && (
              <span className="text-xs" style={{ color: cor }}>
                {nomeDaFaixa(faixa)}
              </span>
            )}
          </div>
          <div className="text-[10px] text-ink-400">
            {esc === 'purpleair' && <>{pmTexto(exibida.valor)} µg/m³, média de 10 min · </>}
            escala {ESCALAS[esc].nome}
          </div>
          {/* A série guarda cada leitura no horário de 5 min mais próximo; a
              mais recente tem o horário exato da rede. Leitura longe do quadro
              exibido: o horário fica em alerta, sem mais texto. */}
          <div className={`text-[10px] tabular-nums ${!noQuadro && !estaParado ? 'text-alerta-300' : 'text-ink-300'}`}>
            {Math.abs(exibida.t - s.ultima.t) <= passoMs / 2 ? 'medido' : 'medido por volta de'}{' '}
            {formatDateTimeBoth(Math.abs(exibida.t - s.ultima.t) <= passoMs / 2 ? s.ultima.t : exibida.t)}
          </div>
        </div>
      ) : (
        <div className="rounded-lg bg-white/5 px-2 py-1.5 text-[11px] leading-snug text-ink-300">
          {estaParado ? (
            <>
              Sem leitura nas últimas 48 h.
              {s.ultima.t > 0 && <> A última que a rede publicou é de {formatDateBrt(s.ultima.t)}.</>}
            </>
          ) : s.fonte === 'purpleair' && s.ultima.flags === 3 ? (
            'Os dois canais do sensor estão degradados: a PurpleAir não mostra valor.'
          ) : (
            'Sem leitura até o quadro exibido.'
          )}
        </div>
      )}

      {temSerie && (
        <div className="pt-1">
          <Grafico
            instantes={instantes}
            iAtual={iAtual}
            linhas={linhas}
            referencias={referencias}
            piso={30}
            rotulo={
              serieMun
                ? 'MP2,5 do sensor nas últimas 48 horas, com a média horária do município'
                : 'MP2,5 do sensor nas últimas 48 horas'
            }
            valor={(i) => {
              const v = s.serie[i];
              const d = serieDesenho[i];
              const m = serieMun?.[i];
              const aqi = (x: number) => (esc === 'purpleair' ? ` (AQI ${valorNaEscala(esc, x)})` : '');
              if (typeof v === 'number') return `${pmTexto(v)} µg/m³${aqi(v)}`;
              if (typeof d === 'number') return `${pmTexto(d)} µg/m³${aqi(d)} (leitura anterior)`;
              if (typeof m === 'number') return `município ${pmTexto(m)} µg/m³`;
              return null;
            }}
            resumo={
              <span className="inline-flex items-center gap-1">
                {serieMun && (
                  <>
                    <svg width="14" height="4" aria-hidden>
                      <line x1="0" y1="2" x2="14" y2="2" stroke="rgba(200,210,225,0.75)" strokeWidth="1.2" strokeDasharray="3 2" />
                    </svg>
                    média do município
                  </>
                )}
              </span>
            }
          />
        </div>
      )}

      <div className="space-y-1 border-t border-white/10 pt-1.5">
        {media24 && (
          <Linha_
            rotulo={media24.de === 'sensor' ? 'Média de 24 h' : 'Média de 24 h (município)'}
            nota={`${media24.valor > OMS_PM25_24H ? 'acima' : 'dentro'} da diretriz da OMS (${OMS_PM25_24H} µg/m³)`}
          >
            <span style={{ color: corDaFaixa(faixaNaEscala(esc, media24.valor)!) }}>
              {pmTexto(media24.valor)} µg/m³{esc === 'purpleair' && <> · AQI {valorNaEscala(esc, media24.valor)}</>} ·{' '}
              {nomeDaFaixa(faixaNaEscala(esc, media24.valor)!)}
            </span>
          </Linha_>
        )}
        {iMun >= 0 && typeof horariaMun?.[iMun] === 'number' && (
          <Linha_ rotulo="Média horária do município">
            {pmTexto(horariaMun[iMun])} µg/m³
          </Linha_>
        )}
        {s.outras.map((o) => {
          const eo = escalaDoSensor(o.fonte);
          const fo = faixaNaEscala(eo, o.pm);
          return (
            <Linha_
              key={o.fonte}
              rotulo={`Na ${NOME_REDE[o.fonte]}`}
              nota={formatTimeBrt(o.t)}
            >
              <span style={{ color: fo ? corDaFaixa(fo) : undefined }}>
                {numeroNaEscala(eo, o.pm)}
                {fo && <> · {nomeDaFaixa(fo)}</>}
              </span>
            </Linha_>
          );
        })}
        {bruto && (
          <Linha_
            rotulo="Canais A e B (RedeAr)"
            titulo="PM2,5 de cada canal do sensor, como a RedeAr publica"
            nota={
              <>
                {s.bruto?.t !== null && s.bruto?.t !== undefined && Math.abs(s.bruto.t - s.ultima.t) > passoMs / 2 && (
                  <>{formatTimeBrt(s.bruto.t)}</>
                )}
                {s.bruto?.flags === FL_AB && <span className="block text-alerta-300">canais divergentes</span>}
              </>
            }
          >
            {bruto}
          </Linha_>
        )}
        {s.met && (
          <Linha_ rotulo="Temperatura · umidade" titulo="Medidas dentro do sensor: leem mais quente e mais seco que o ar">
            {[
              s.met.temp !== null ? `${s.met.temp.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} °C` : null,
              s.met.ur !== null ? `${Math.round(s.met.ur)}%` : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </Linha_>
        )}
      </div>

      {s.ultima.flags !== null && s.ultima.flags > 0 && s.ultima.flags < 3 && (
        <div className="text-[10px] text-alerta-300">Um dos canais do sensor estava degradado na leitura mais recente.</div>
      )}

      {/* Só a fonte deste ponto: as outras redes que repassam o aparelho têm a linha delas acima. */}
      {/* A previsão deste lugar (o "Próximos dias"). */}
      <ProximosDiasAqui lon={s.lon} lat={s.lat} />
      <div className="border-t border-white/10 pt-1 text-[11px] text-ink-400">
        {formatLonLat(s.lon, s.lat)} · <Fonte href={linkDoSensor(s)}>{rotuloRef(s)}</Fonte>
        {s.fonte === 'purpleair' && (
          <>
            {' · '}
            <Fonte href={PURPLEAIR_URL}>{PURPLEAIR_ATRIBUICAO}</Fonte>
          </>
        )}
        {s.licenca && <> · {s.licenca}</>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Estação do MonitorAr

function FichaEstacao({ inicial }: { inicial: Estacao }) {
  const e = useArStore((st) => st.pontos.estacoes.find((x) => x.id === inicial.id)) ?? inicial;
  const h0 = useArStore((st) => st.pontos.h0);
  const instante = useInstante();
  const n = e.horas.length;

  const noQuadro = ultimoAte<IqarHora>(e.horas, h0, HOUR_MS, instante, TOLERANCIA_ESTACAO_MS);
  const exibida =
    noQuadro ??
    ultimoAte<IqarHora>(e.horas, h0, HOUR_MS, instante ?? Date.now(), Infinity) ??
    (e.atual && e.atual.faixa && (instante === null || e.atual.t <= instante)
      ? { valor: { iqar: e.atual.iqar, faixa: e.atual.faixa, poluente: e.atual.poluente ?? '' }, t: e.atual.t, i: -1 }
      : null);
  const faixa = exibida?.valor.faixa ?? null;
  const cor = faixa ? corDaFaixa(faixa) : COR_SEM_DADO;
  const estaParado = parado(e.ultima);
  const iAtual = indiceQueContem(n, h0, HOUR_MS, instante);
  const iLido = exibida && exibida.i >= 0 ? exibida.i : -1;

  const linhas = useMemo<Linha[]>(
    () => e.poluentes.map((p, k) => ({ valores: p.iqar, cor: COR_POLUENTE[k % COR_POLUENTE.length]! })),
    [e.poluentes],
  );
  const instantes = useMemo(() => Array.from({ length: n }, (_, i) => h0 + i * HOUR_MS), [n, h0]);
  const principal = Math.max(
    0,
    e.poluentes.findIndex((p) => p.nome === exibida?.valor.poluente),
  );

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5 text-xs text-ink-400">
        <Simbolo forma="losango" cor={faixa ? cor : null} tamanho={13} anel={!!faixa} />
        Qualidade do ar · estação oficial (MonitorAr)
      </div>

      <div className="leading-tight font-semibold text-white">
        {e.nome}
        <div className="text-[11px] font-normal text-ink-400">
          {[e.municipio, e.uf].filter(Boolean).join(' – ')}
          {e.orgao && <> · {e.orgao}</>}
        </div>
      </div>

      {exibida ? (
        <div>
          <div className="flex items-baseline gap-2 pt-0.5">
            <span className="text-xl font-semibold tabular-nums" style={{ color: cor }}>
              IQAr {Math.round(exibida.valor.iqar)}
            </span>
            {faixa && (
              <span className="text-xs" style={{ color: cor }}>
                {nomeDaFaixa(faixa)}
                {exibida.valor.poluente && <span className="text-ink-300"> · pior poluente {exibida.valor.poluente}</span>}
              </span>
            )}
          </div>
          <div className="text-[10px] text-ink-400">escala {ESCALAS.iqar.nome}</div>
          <div className={`text-[10px] tabular-nums ${!noQuadro && !estaParado ? 'text-alerta-300' : 'text-ink-300'}`}>
            medição horária de {formatDateTimeBoth(exibida.t)}
          </div>
        </div>
      ) : (
        <div className="rounded-lg bg-white/5 px-2 py-1.5 text-[11px] leading-snug text-ink-300">
          Sem dado recente.
          {e.ultima !== null ? <> A última atualização no MonitorAr é de {formatDateBrt(e.ultima)}.</> : ' O MonitorAr não informa uma data válida.'}
        </div>
      )}

      {e.poluentes.length > 0 && (
        <div className="pt-1">
          <Grafico
            instantes={instantes}
            iAtual={iAtual}
            linhas={linhas}
            referencias={REF_IQAR}
            piso={50}
            principal={principal}
            rotulo="IQAr de cada poluente nas últimas 48 horas"
            valor={(i) => {
              const partes = e.poluentes
                .map((p) => (typeof p.iqar[i] === 'number' ? `${p.nome} ${Math.round(p.iqar[i]!)}` : null))
                .filter(Boolean);
              return partes.length ? partes.join(' · ') : null;
            }}
            resumo="IQAr"
          />
          <div className="mt-1 flex flex-wrap gap-x-2.5 gap-y-0.5 text-[10px] text-ink-300">
            {e.poluentes.map((p, k) => (
              <span key={p.nome} className="inline-flex items-center gap-1" title={p.descricao ?? undefined}>
                <span className="inline-block h-0.5 w-3 rounded" style={{ background: COR_POLUENTE[k % COR_POLUENTE.length] }} />
                {p.nome}
              </span>
            ))}
          </div>
        </div>
      )}

      {iLido >= 0 && (
        <div className="space-y-1 border-t border-white/10 pt-1.5">
          {e.poluentes.map((p) => {
            const v = p.iqar[iLido];
            if (typeof v !== 'number') return null;
            const f = faixaDaEstacao(v, p.classe[iLido] ?? null);
            return (
              <Linha_ key={p.nome} rotulo={p.nome} titulo={p.descricao}>
                <span style={{ color: f ? corDaFaixa(f) : undefined }}>
                  {Math.round(v)} · {f ? nomeDaFaixa(f) : '—'}
                  {p.validado[iLido] === 0 && <span className="text-ink-400"> (não validado)</span>}
                </span>
              </Linha_>
            );
          })}
        </div>
      )}

      {/* A previsão deste lugar (o "Próximos dias"). */}
      <ProximosDiasAqui lon={e.lon} lat={e.lat} />
      <div className="border-t border-white/10 pt-1 text-[11px] text-ink-400">
        {formatLonLat(e.lon, e.lat)} · estação {e.id} · <Fonte href={MONITORAR_URL}>MonitorAr/MMA</Fonte>
      </div>
    </div>
  );
}

export function ArPopup({ dado }: { dado: PontoFeature }) {
  return dado.ponto.tipo === 'sensor' ? <FichaSensor inicial={dado.ponto} /> : <FichaEstacao inicial={dado.ponto} />;
}
